import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 } from 'uuid';
import './style.less';
import clsx from 'clsx';
import { Icon, IconName } from 'shared/components/Icon';
import { Button } from 'shared/components/Button';
import { CallTask, CallTaskStatus, SourceType } from 'types/outbound';
import { createLbbContact, searchLBBContacts } from 'shared/api';
import { useOutboundContext } from '../../outbound-provider';

const allowedInputs = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#', '+'];

export const Dialpad: React.FC<{
  onDialClick: (task: CallTask) => Promise<void>;
  open?: boolean;
  callBtnDisabled?: boolean;
}> = ({ onDialClick, open, callBtnDisabled }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState<string>('');
  const [pressedKey, setPressedKey] = useState<string | undefined | null>(null);

  const { session } = useOutboundContext();

  const startDial = useCallback(async () => {
    const { data } = await searchLBBContacts(value);
    if (data.length) {
      const contact = data[0];
      await onDialClick({
        frontendId: v4(),
        sourceId: -1,
        sourceType: SourceType.DIALPAD,
        status: CallTaskStatus.PENDING,
        contact: {
          name: contact.name,
          firstName: contact.firstName,
          lastName: contact.lastName,
          id: contact.id,
          company: contact.company,
          email: contact.email,
          phone: contact.phone,
          phoneNumbers: contact.phoneNumbers,
          customMetadata: contact.customMetadata,
          associatedServices: contact.associatedServices,
        },
      });
      return;
    } else {
      const { data } = await createLbbContact({ phone: value });
      await onDialClick({
        frontendId: v4(),
        sourceId: -1,
        sourceType: SourceType.DIALPAD,
        status: CallTaskStatus.PENDING,
        contact: {
          name: data.name,
          firstName: data.firstName,
          lastName: data.lastName,
          id: data.id,
          company: data.company,
          email: data.email,
          phone: data.phone,
          phoneNumbers: data.phoneNumbers,
          customMetadata: data.customMetadata as Record<string, string | number>,
          associatedServices: data.associatedServices,
        },
      });
    }
  }, [onDialClick, value]);

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      setPressedKey(event.key);

      if (event.key === 'Enter' && !!value && !callBtnDisabled) {
        startDial();
      }

      if (session?.current && allowedInputs.includes(event.key)) {
        session.current.twilioInstance?.call?.sendDigits(event.key?.toString());
      }
    },
    [value, callBtnDisabled, session, startDial],
  );

  const handleKeyRelease = () => {
    setPressedKey(null);
  };

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    window.addEventListener('keyup', handleKeyRelease);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      window.removeEventListener('keyup', handleKeyRelease);
    };
  }, [handleKeyPress]);

  return (
    <div className="Dialpad">
      <input
        className="Dialpad__input"
        value={value}
        onChange={e => {
          const filteredValue = e.target.value
            .split('')
            .filter(char => allowedInputs.includes(char))
            .join('');

          setValue(filteredValue);
        }}
        ref={inputRef}
        type="text"
      />
      <div className="Dialpad__buttons">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, '*', 0, '#', '+'].map(v => {
          const useIcon = ['*', '#', '+'].includes(v as string);

          const icons: Record<string, IconName> = {
            '*': 'asterix',
            '#': 'hash',
            '+': 'plusSlim',
          };

          const subtitle: Record<number, string> = {
            1: ' ',
            2: 'ABC',
            3: 'DEF',
            4: 'GHI',
            5: 'JKL',
            6: 'MNO',
            7: 'PQRS',
            8: 'TUV',
            9: 'WXYZ',
          };

          return (
            <button
              key={v}
              onClick={e => {
                e.preventDefault();

                if (session?.current) {
                  session.current.twilioInstance?.call?.sendDigits(v?.toString());
                }

                setValue(val => `${val}${v}`);
              }}
              className={clsx(
                'Dialpad__button',
                v == pressedKey &&
                  document.activeElement === inputRef.current &&
                  'Dialpad__button--active',
              )}
            >
              <div className="Dialpad__button-inner">
                {!useIcon ? v : <Icon width={20} height={20} icon={icons[v]} />}
                {subtitle[v as number] && <span>{subtitle[v as number]}</span>}
              </div>
            </button>
          );
        })}
        <Button
          className="Dialpad__button--call"
          type="primary"
          disabled={!value || callBtnDisabled}
          onClick={startDial}
        >
          <Icon icon="phoneOn" width={24} height={24} />
        </Button>
        <button
          className={clsx('Dialpad__button')}
          onClick={() => setValue(val => val.slice(0, -1))}
        >
          <Icon icon="erase" width={26} height={20} />
        </button>
      </div>
    </div>
  );
};
